import React, { useState, useEffect, useCallback, useRef } from 'react';
import Map, {
  Marker,
  Source,
  Layer,
  NavigationControl,
  GeolocateControl,
  Popup
} from 'react-map-gl/mapbox';
import type { MapRef } from 'react-map-gl/mapbox';
import { FeatureCollection, Point } from 'geojson';
import { ButtonGroup, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMap, faList, faTh, faCalendarCheck } from '@fortawesome/free-solid-svg-icons';
import 'mapbox-gl/dist/mapbox-gl.css';
import PropertyHoverCard from './PropertyHoverCard';
import VoiceControl from './VoiceControl';
import LayerControls from './LayerControls';
import MapStyleControls from './MapStyleControls';
import PropertySearchBar from './PropertySearchBar';
import PropertyCarousel from './PropertyCarousel';
import PropertyDetailsPanel from './PropertyDetailsPanel';
import PropertyList from './PropertyList';
import AgentK from './AgentK';
import PropertyDetailsModal from './PropertyDetailsModal';
import TourSchedulingModal from './TourSchedulingModal';
import axiosInstance from '../../helpers/api';
import { mapEventBus } from '../../utils/mapEventBus';
import toast from 'react-hot-toast';
import './MapView.css';
import { Property } from '../../types/property';

// Mapbox token from env file
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || '';
if (!MAPBOX_TOKEN) {
  console.error('❌ REACT_APP_MAPBOX_TOKEN is missing! Map will not load.');
  console.error('Please add REACT_APP_MAPBOX_TOKEN to your .env file and restart the dev server.');
} else {
  console.log('🔑 Mapbox token loaded successfully ✓');
}

interface MapViewProps {
  properties: Property[];
  onPropertySelect: (property: Property) => void;
  thetaInsights?: any;
  showCarousel?: boolean;
  carouselTitle?: string;
  agentPhoto?: string;
  agentName?: string;
  initialSearchFilters?: any;
  onToggleSave?: (property: Property) => void;
  savedPropertyIds?: Set<string>;
  sellerProperty?: {
    address: string;
    leadId?: string;
    agentId?: string;
    valuation?: any;
  };
  showSellerOverlay?: boolean;
}

const MapView: React.FC<MapViewProps> = ({ 
  properties, 
  onPropertySelect, 
  thetaInsights,
  showCarousel = false,
  carouselTitle,
  agentPhoto,
  agentName,
  initialSearchFilters,
  onToggleSave,
  savedPropertyIds = new Set(),
  sellerProperty,
  showSellerOverlay = false
}) => {
  const mapRef = useRef<MapRef>(null);
  
  // Declare mapLoaded first to avoid initialization errors
  const [mapLoaded, setMapLoaded] = useState(false);
  
  const [searchFilters, setSearchFilters] = useState<any>(initialSearchFilters || {});
  const [filteredProperties, setFilteredProperties] = useState(properties);
  const [visibleProperties, setVisibleProperties] = useState<Property[]>([]);
  
  // Debug logging
  useEffect(() => {
    console.log('🗺️ MapView component mounted');
    console.log('🔑 Mapbox token available:', !!MAPBOX_TOKEN);
    console.log('🗺️ MapView received properties:', properties?.length || 0);
    if (properties?.length > 0) {
      console.log('📍 First property:', properties[0]);
      console.log('📍 First property coordinates:', properties[0].coordinates);
    }
    return () => {
      console.log('🗺️ MapView component unmounting');
    };
  }, [properties]);

  // Update filteredProperties when properties prop changes
  useEffect(() => {
    console.log('🔄 Properties prop changed, updating filteredProperties:', properties?.length || 0);
    setFilteredProperties(properties || []);
    // NO AUTO-CENTERING - Map stays where Theta AI searched
  }, [properties]);

  // Listen for map center requests from sessionStorage
  useEffect(() => {
    const checkCenterRequest = () => {
      const centerRequest = sessionStorage.getItem('mapCenterRequest');
      if (centerRequest && mapRef.current && mapLoaded) {
        try {
          const request = JSON.parse(centerRequest);
          console.log('🎯 Processing map center request:', request);
          
          mapRef.current.flyTo({
            center: request.center,
            zoom: request.zoom || 12,
            duration: 1500,
            essential: true
          });
          
          // Clear the request
          sessionStorage.removeItem('mapCenterRequest');
        } catch (error) {
          console.error('Error processing map center request:', error);
        }
      }
    };

    // Check immediately and on interval
    checkCenterRequest();
    const interval = setInterval(checkCenterRequest, 500);

    return () => clearInterval(interval);
  }, [mapLoaded]);
  
  // Initialize view state based on search filters if available
  const getInitialViewState = () => {
    // Check for city in search filters
    if (initialSearchFilters?.city) {
      const cityLower = initialSearchFilters.city.toLowerCase();
      const cityCoords: { [key: string]: { lng: number, lat: number } } = {
        'gilbert': { lng: -111.7890, lat: 33.3528 },
        'scottsdale': { lng: -111.9261, lat: 33.4942 },
        'tempe': { lng: -111.9400, lat: 33.4255 },
        'mesa': { lng: -111.8315, lat: 33.4152 },
        'chandler': { lng: -111.8413, lat: 33.3062 }
      };
      
      if (cityCoords[cityLower]) {
        console.log('🎯 Initializing map view for city:', initialSearchFilters.city);
        return {
          longitude: cityCoords[cityLower].lng,
          latitude: cityCoords[cityLower].lat,
          zoom: 12,
          pitch: 0,
          bearing: 0
        };
      }
    }
    
    // Default to downtown Phoenix if no specific city
    return {
      longitude: -112.0740,  // Downtown Phoenix longitude
      latitude: 33.4484,     // Downtown Phoenix latitude
      zoom: 13,              // Closer zoom for downtown
      pitch: 0,
      bearing: 0
    };
  };
  
  const [viewState, setViewState] = useState(getInitialViewState());
  
  const [hoveredProperty, setHoveredProperty] = useState<Property | null>(null);
  const [highlightedProperty, setHighlightedProperty] = useState<Property | null>(null);
  const [selectedProperty, setSelectedProperty] = useState<Property | null>(null);
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [showPropertyCarousel, setShowPropertyCarousel] = useState(showCarousel);
  const [tourPropertyIds, setTourPropertyIds] = useState<Set<string>>(() => {
    // Load tour properties from localStorage
    const saved = localStorage.getItem('tourProperties');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [showTourSchedulingModal, setShowTourSchedulingModal] = useState(false);
  
  // View mode state
  type ViewMode = 'map' | 'browse' | 'split';
  const [viewMode, setViewMode] = useState<ViewMode>('map');
  const [showSidebar, setShowSidebar] = useState(false); // Always start with sidebar hidden
  const [currentZoom, setCurrentZoom] = useState(11);
  const [previousZoom, setPreviousZoom] = useState(11);
  const zoomNotificationSent = useRef(false);
  
  // Map style state with localStorage persistence
  const mapStyles = {
    dark: 'mapbox://styles/mapbox/dark-v11',
    light: 'mapbox://styles/mapbox/light-v11',
    streets: 'mapbox://styles/mapbox/streets-v12',
    satellite: 'mapbox://styles/mapbox/satellite-streets-v12'
  } as const;

  const [mapStyle, setMapStyle] = useState<keyof typeof mapStyles>(() => {
    const savedStyle = localStorage.getItem('mapStyle');
    // Validate that the saved style is a valid key in mapStyles
    if (savedStyle && savedStyle in mapStyles) {
      return savedStyle as keyof typeof mapStyles;
    }
    // Default to 'dark' if invalid or missing
    return 'dark';
  });

  // Get the actual map style URL, ensuring it's always a valid string
  const getMapStyleUrl = (): string => {
    const styleUrl = mapStyles[mapStyle];
    if (typeof styleUrl === 'string' && styleUrl.startsWith('mapbox://')) {
      return styleUrl;
    }
    // Fallback to dark style if something goes wrong
    console.warn('Invalid map style, falling back to dark:', mapStyle);
    return mapStyles.dark;
  };

  // Save map style preference
  const handleStyleChange = (style: string) => {
    // Only save if it's a valid style key
    if (style in mapStyles) {
      setMapStyle(style as keyof typeof mapStyles);
      localStorage.setItem('mapStyle', style);
    }
  };
  
  // Update visible properties based on map bounds
  const updateVisibleProperties = useCallback(() => {
    if (!mapRef.current) return;

    const map = mapRef.current.getMap();
    const bounds = map.getBounds();

    // Ensure bounds is valid before filtering
    if (!bounds) return;

    // Filter properties that are within the current bounds
    const visible = filteredProperties.filter(property => {
      // Handle both nested and flat coordinate formats
      const lng = property.coordinates?.longitude || property.longitude;
      const lat = property.coordinates?.latitude || property.latitude;

      // Check for valid coordinates before trying to use bounds.contains
      if (!lng || !lat || isNaN(lng) || isNaN(lat)) {
        return false;
      }

      try {
        return bounds.contains([lng, lat]);
      } catch (error) {
        console.warn('Invalid coordinates for property:', property.mlsNumber || property.id, { lng, lat });
        return false;
      }
    });
    
    // Sort by daysOnMarket (most recently added first - lowest days first)
    visible.sort((a, b) => {
      // If both have daysOnMarket, sort ascending (newest first)
      if (a.daysOnMarket !== undefined && b.daysOnMarket !== undefined) {
        return a.daysOnMarket - b.daysOnMarket;
      }
      // If only a has daysOnMarket, it comes first
      if (a.daysOnMarket !== undefined) return -1;
      // If only b has daysOnMarket, it comes first
      if (b.daysOnMarket !== undefined) return 1;
      // If neither have daysOnMarket, maintain original order
      return 0;
    });
    
    setVisibleProperties(visible);
    console.log(`🗺️ Visible properties: ${visible.length} of ${filteredProperties.length}`);
  }, [filteredProperties]);
  
  // Layer visibility states
  const [layers, setLayers] = useState({
    schools: true,
    priceHeatmap: false,
    commuteTimes: false,
    futureValue: false
  });

  // Zoom-based sidebar logic and chatbot notification
  useEffect(() => {
    // Auto-show sidebar when zoomed into neighborhood level
    if (viewMode === 'map' && currentZoom > 14) {
      setShowSidebar(true);
    } else if (viewMode === 'map' && currentZoom <= 14) {
      setShowSidebar(false);
    }

    // Notify backend when zoom reaches level 14
    if (Math.floor(currentZoom) === 14 && Math.floor(previousZoom) < 14 && !zoomNotificationSent.current) {
      console.log('🔍 User reached zoom level 14, notifying chatbot...');
      zoomNotificationSent.current = true;
      
      // Get auth token
      const authToken = localStorage.getItem('customerPortalToken');
      const sessionId = sessionStorage.getItem('customerPortalSessionId') || 'map-session-' + Date.now();
      
      // Notify backend about zoom change
      axiosInstance.post('/map-events/zoom-change', {
        zoomLevel: 14,
        previousZoomLevel: previousZoom,
        visibleProperties: visibleProperties.map(p => ({
          id: p.id || p.mlsNumber,
          price: p.price,
          bedrooms: p.bedrooms,
          address: (p as any).address || { city: p.city, state: p.state, zip: p.zipCode }
        })),
        mapBounds: mapRef.current ? (() => {
          const bounds = mapRef.current?.getMap()?.getBounds();
          return bounds ? {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest()
          } : null;
        })() : null,
        sessionId: sessionId
      }, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      }).then(response => {
        console.log('✅ Zoom notification sent:', response.data);
        
        // If the response includes a property tour prompt, show it in the chat
        if (response.data?.data?.type === 'property_tour_prompt') {
          // Emit event for ThetaAISearch component to display the prompt
          mapEventBus.emit('property_tour_prompt', response.data.data);
        }
      }).catch(error => {
        console.error('❌ Failed to notify zoom change:', error);
      });
    }
    
    // Reset notification flag when zooming away from 14
    if (Math.floor(currentZoom) !== 14) {
      zoomNotificationSent.current = false;
    }
  }, [currentZoom, previousZoom, viewMode, visibleProperties]);

  // Apply search filters
  useEffect(() => {
    let filtered = properties;
    
    // Filter by price
    if (searchFilters.minPrice || searchFilters.priceMin) {
      const minPrice = searchFilters.minPrice || searchFilters.priceMin;
      filtered = filtered.filter(p => (p.price || 0) >= minPrice);
    }
    if (searchFilters.maxPrice || searchFilters.priceMax) {
      const maxPrice = searchFilters.maxPrice || searchFilters.priceMax;
      filtered = filtered.filter(p => (p.price || 0) <= maxPrice);
    }
    
    // Filter by bedrooms
    if (searchFilters.bedrooms || searchFilters.beds) {
      const bedrooms = searchFilters.bedrooms || searchFilters.beds;
      filtered = filtered.filter(p => (p.bedrooms || 0) >= bedrooms);
      console.log(`🛏️ Filtering by bedrooms: >= ${bedrooms}, remaining: ${filtered.length}`);
    }
    
    // Filter by bathrooms
    if (searchFilters.bathrooms || searchFilters.baths) {
      const bathrooms = searchFilters.bathrooms || searchFilters.baths;
      filtered = filtered.filter(p => (p.bathrooms || 0) >= bathrooms);
    }
    
    // Filter by property type
    if (searchFilters.propertyType && searchFilters.propertyType !== 'all') {
      filtered = filtered.filter(p => p.propertyType === searchFilters.propertyType);
    }
    
    // Filter by city (from AI search)
    if (searchFilters.city) {
      console.log(`🏙️ Filtering by city: ${searchFilters.city}`);
      filtered = filtered.filter(p => 
        (p.city || (p as any).address?.city)?.toLowerCase() === searchFilters.city.toLowerCase()
      );
      console.log(`🏙️ After city filter: ${filtered.length} properties`);
    }
    
    // Filter by location (simple contains check)
    // Only apply location filter if it's actually specified and not the default
    if (searchFilters.location && 
        searchFilters.location !== 'Phoenix, AZ' && 
        !searchFilters.city && 
        searchFilters.location.trim() !== '') {
      filtered = filtered.filter(p => 
        (p.city || (p as any).address?.city)?.toLowerCase().includes(searchFilters.location.toLowerCase()) ||
        (p.zipCode || (p as any).address?.zip)?.includes(searchFilters.location)
      );
    }
    
    // Filter by square footage
    if (searchFilters.minSqft) {
      filtered = filtered.filter(p => (p.squareFeet || (p as any).sqft) >= searchFilters.minSqft);
    }
    if (searchFilters.maxSqft) {
      filtered = filtered.filter(p => (p.squareFeet || (p as any).sqft) <= searchFilters.maxSqft);
    }
    
    // Filter by lot size (if available)
    if (searchFilters.minLot) {
      filtered = filtered.filter(p => (p as any).lotSize >= searchFilters.minLot);
    }
    
    console.log(`🔍 Applied filters:`, searchFilters);
    console.log(`🔍 Results: ${properties.length} → ${filtered.length} properties`);
    
    // Debug: Show some sample properties
    if (filtered.length === 0 && properties.length > 0) {
      console.log('⚠️ No properties match filters! Sample properties:', properties.slice(0, 3).map(p => ({
        city: p.city || (p as any).address?.city,
        price: p.price,
        bedrooms: p.bedrooms,
        bathrooms: p.bathrooms
      })));
    }
    
    setFilteredProperties(filtered);
  }, [searchFilters, properties]);

  // Update carousel visibility when prop changes
  useEffect(() => {
    setShowPropertyCarousel(showCarousel);
  }, [showCarousel]);

  // Center map on seller's property when available
  useEffect(() => {
    if (showSellerOverlay && sellerProperty && sellerProperty.valuation) {
      const { longitude, latitude } = sellerProperty.valuation;
      if (longitude && latitude) {
        console.log('🏡 Centering map on seller property:', sellerProperty.address);
        // Use flyTo for smooth animation instead of setViewState
        mapRef.current?.flyTo({
          center: [longitude, latitude],
          zoom: 15, // Closer zoom for seller's property
          pitch: 0,
          bearing: 0,
          duration: 1000 // Smooth transition
        });
      }
    }
  }, [showSellerOverlay, sellerProperty]);


  // Handle search
  const handleSearch = (filters: any) => {
    console.log('🔍 Search filters:', filters);
    setSearchFilters(filters);
    
    // Zoom to filtered area if location changed
    if (filters.location && filters.location !== searchFilters.location) {
      // Simple location mapping
      const locationCoords: { [key: string]: { lat: number, lng: number } } = {
        'scottsdale': { lat: 33.4942, lng: -111.9261 },
        'tempe': { lat: 33.4255, lng: -111.9400 },
        'mesa': { lat: 33.4152, lng: -111.8315 },
        'chandler': { lat: 33.3062, lng: -111.8413 },
        'gilbert': { lat: 33.3528, lng: -111.7890 }
      };
      
      const location = filters.location.toLowerCase();
      Object.keys(locationCoords).forEach(city => {
        if (location.includes(city)) {
          mapRef.current?.flyTo({
            center: [locationCoords[city].lng, locationCoords[city].lat],
            zoom: 12,
            duration: 2000
          });
        }
      });
    }
  };

  const handleClearFilters = () => {
    setSearchFilters({});
    setFilteredProperties(properties);
  };

  // Convert properties to GeoJSON, filtering out invalid coordinates
  const validProperties = filteredProperties.filter(p => {
    // Handle both nested coordinates and flat longitude/latitude
    const lng = p.coordinates?.longitude || p.longitude;
    const lat = p.coordinates?.latitude || p.latitude;

    const isValid = lng && lat && !isNaN(lng) && !isNaN(lat) && lng !== 0 && lat !== 0;

    if (!isValid && p.mlsNumber) {
      console.log('⚠️ Invalid coordinates for property:', p.mlsNumber, { lng, lat });
    }

    return isValid;
  });

  console.log(`📍 Valid properties for map display: ${validProperties.length}/${filteredProperties.length}`);

  const propertyGeoJSON: FeatureCollection<Point, Property> = {
    type: 'FeatureCollection',
    features: validProperties.map(property => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [
          property.coordinates?.longitude || property.longitude || 0,
          property.coordinates?.latitude || property.latitude || 0
        ]
      },
      properties: property
    }))
  };

  // Cluster properties for performance
  const clusterLayer: any = {
    id: 'clusters',
    type: 'circle',
    source: 'properties',
    filter: ['has', 'point_count'],
    paint: {
      'circle-color': [
        'step',
        ['get', 'point_count'],
        '#51bbd6',
        100,
        '#f1f075',
        750,
        '#f28cb1'
      ],
      'circle-radius': [
        'step',
        ['get', 'point_count'],
        20, 100,
        30, 750,
        40
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#ffffff'
    }
  };

  const clusterCountLayer: any = {
    id: 'cluster-count',
    type: 'symbol',
    source: 'properties',
    filter: ['has', 'point_count'],
    layout: {
      'text-field': '{point_count_abbreviated}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': 12
    },
    paint: {
      'text-color': '#ffffff'
    }
  };

  const unclusteredPointLayer: any = {
    id: 'unclustered-point',
    type: 'circle',
    source: 'properties',
    filter: ['!', ['has', 'point_count']],
    paint: {
      'circle-color': [
        'case',
        ['<', ['get', 'daysOnMarket'], 3], '#ef4444',     // Hot! Just listed - red
        ['<', ['get', 'daysOnMarket'], 7], '#10b981',     // New listing - green
        ['<', ['get', 'price'], 400000], '#3b82f6',       // Great deal - blue
        ['>', ['get', 'price'], 1000000], '#fbbf24',      // Luxury - gold
        '#8b5cf6' // Default - purple
      ],
      'circle-radius': [
        'case',
        ['<', ['get', 'daysOnMarket'], 3], 12,  // Bigger for hot properties
        8
      ],
      'circle-stroke-width': [
        'case',
        ['<', ['get', 'daysOnMarket'], 3], 3,   // Thicker border for hot
        2
      ],
      'circle-stroke-color': '#ffffff',
      'circle-opacity': 0.9
    }
  };

  // Handle voice commands
  const handleVoiceCommand = useCallback((command: string) => {
    console.log('🎤 Voice command received:', command);
    const lowerCommand = command.toLowerCase();
    
    // Parse natural language commands
    if (lowerCommand.includes('school')) {
      setLayers(prev => ({ ...prev, schools: true }));
      console.log('✅ Enabled school quality layer');
    }
    
    // Location-based commands
    if (lowerCommand.includes('scottsdale')) {
      mapRef.current?.flyTo({
        center: [-111.9261, 33.4942],
        zoom: 12,
        duration: 2000
      });
      console.log('✅ Flying to Scottsdale');
    } else if (lowerCommand.includes('phoenix')) {
      mapRef.current?.flyTo({
        center: [-112.0740, 33.4484],
        zoom: 11,
        duration: 2000
      });
      console.log('✅ Flying to Phoenix');
    } else if (lowerCommand.includes('tempe')) {
      mapRef.current?.flyTo({
        center: [-111.9400, 33.4255],
        zoom: 12,
        duration: 2000
      });
      console.log('✅ Flying to Tempe');
    }
    
    // Price-based commands
    if (lowerCommand.includes('under 500') || lowerCommand.includes('under five hundred')) {
      // Show price heatmap
      setLayers(prev => ({ ...prev, priceHeatmap: true }));
      console.log('✅ Showing properties under $500k');
    }
    
    // Layer commands
    if (lowerCommand.includes('show all layers') || lowerCommand.includes('enable all')) {
      setLayers({
        schools: true,
        priceHeatmap: true,
        commuteTimes: true,
        futureValue: true
      });
      console.log('✅ All layers enabled');
    } else if (lowerCommand.includes('hide all layers') || lowerCommand.includes('disable all')) {
      setLayers({
        schools: false,
        priceHeatmap: false,
        commuteTimes: false,
        futureValue: false
      });
      console.log('✅ All layers disabled');
    }
    
    // Zoom commands
    if (lowerCommand.includes('zoom in') || lowerCommand.includes('closer')) {
      mapRef.current?.zoomIn();
    } else if (lowerCommand.includes('zoom out') || lowerCommand.includes('farther')) {
      mapRef.current?.zoomOut();
    }
    
    // Show AI predictions
    if (lowerCommand.includes('future') || lowerCommand.includes('prediction')) {
      setLayers(prev => ({ ...prev, futureValue: true }));
      console.log('✅ Showing future value predictions');
    }
  }, []);

  // Handle map click
  const handleMapClick = useCallback((event: any) => {
    console.log('🖱️ Map clicked:', event);
    const features = event.features;
    console.log('🎯 Features:', features);
    if (!features || features.length === 0) return;
    
    const feature = features[0];
    console.log('📍 Feature clicked:', feature);
    
    // Handle seller property click
    if (feature.layer && (feature.layer.id === 'seller-property-bg' || feature.layer.id === 'seller-property-icon')) {
      console.log('🏡 Seller property clicked');
      if (sellerProperty && sellerProperty.valuation) {
        // Create a property object for the modal
        const sellerPropertyData: Property = {
          mlsNumber: `SELLER-${Date.now()}`,
          price: sellerProperty.valuation.estimated_value || 0,
          bedrooms: sellerProperty.valuation.bedrooms || 0,
          bathrooms: sellerProperty.valuation.bathrooms || 0,
          sqft: sellerProperty.valuation.sqft || 0,
          address: {
            street: sellerProperty.address.split(',')[0] || '',
            city: sellerProperty.valuation.city || 'Phoenix',
            state: 'AZ',
            zip: sellerProperty.valuation.zip || ''
          },
          coordinates: {
            longitude: sellerProperty.valuation.longitude,
            latitude: sellerProperty.valuation.latitude
          },
          images: [],
          thumbnails: [],
          daysOnMarket: 0,
          propertyType: sellerProperty.valuation.property_type || 'Single Family',
          yearBuilt: sellerProperty.valuation.year_built,
          description: `Your property at ${sellerProperty.address}. Estimated value based on AI analysis and market comparables.`,
          features: []
        };
        
        setSelectedProperty(sellerPropertyData);
        setShowDetailsModal(true);
        onPropertySelect(sellerPropertyData);
      }
      return;
    }
    
    // Handle cluster click
    if (feature.properties.cluster) {
      console.log('🔵 Cluster clicked');
      const clusterId = feature.properties.cluster_id;
      const mapboxSource = mapRef.current?.getSource('properties') as any;
      
      // Zoom into cluster
      mapboxSource.getClusterExpansionZoom(clusterId, (err: any, zoom: number) => {
        if (!err) {
          mapRef.current?.easeTo({
            center: feature.geometry.coordinates,
            zoom: zoom,
            duration: 1000
          });
        }
      });
    } 
    // Handle single property click
    else {
      console.log('🏠 Single property clicked');
      const property = feature.properties as Property;
      
      // Fix images array if it was serialized as a string
      if (typeof property.images === 'string') {
        try {
          property.images = JSON.parse(property.images);
        } catch (e) {
          console.warn('Could not parse images string:', e);
          property.images = [];
        }
      }
      
      // Fix features array if it was serialized as a string
      if (typeof (property as any).features === 'string') {
        try {
          (property as any).features = JSON.parse((property as any).features);
        } catch (e) {
          console.warn('Could not parse features string:', e);
          (property as any).features = [];
        }
      }
      
      // Fix thumbnails array if it was serialized as a string
      if (typeof (property as any).thumbnails === 'string') {
        try {
          (property as any).thumbnails = JSON.parse((property as any).thumbnails);
        } catch (e) {
          console.warn('Could not parse thumbnails string:', e);
          (property as any).thumbnails = [];
        }
      }
      
      // Fix address if it was serialized as a string
      if (typeof (property as any).address === 'string') {
        try {
          (property as any).address = JSON.parse((property as any).address);
        } catch (e) {
          console.warn('Could not parse address string:', e);
          (property as any).address = {
            street: '',
            city: '',
            state: 'AZ',
            zip: ''
          };
        }
      }
      
      // Fix listingAgent if it was serialized as a string
      if (typeof (property as any).listingAgent === 'string') {
        try {
          (property as any).listingAgent = JSON.parse((property as any).listingAgent);
        } catch (e) {
          console.warn('Could not parse listingAgent string:', e);
          (property as any).listingAgent = undefined;
        }
      }
      
      console.log('🏠 Property from map click:', property);
      console.log('🏠 Property images after fix:', property.images);
      console.log('🏠 Property images type:', typeof property.images);
      console.log('🏠 Property images is array?:', Array.isArray(property.images));
      setSelectedProperty(property);
      setShowDetailsModal(true); // Show modal instead of panel
      console.log('✅ Show details modal');
      onPropertySelect(property);
    }
  }, [onPropertySelect, sellerProperty]);

  // Handle mouse move for hover
  const handleMouseMove = useCallback((event: any) => {
    const { point, features } = event;
    setCursorPosition({ x: point.x, y: point.y });
    
    const feature = features?.[0];
    if (feature && !feature.properties.cluster) {
      const property = feature.properties as Property;
      
      // Fix arrays if they were serialized as strings (same as in click handler)
      if (typeof property.images === 'string') {
        try {
          property.images = JSON.parse(property.images);
        } catch (e) {
          property.images = [];
        }
      }
      
      if (typeof property.features === 'string') {
        try {
          property.features = JSON.parse(property.features);
        } catch (e) {
          property.features = [];
        }
      }
      
      if (typeof property.thumbnails === 'string') {
        try {
          property.thumbnails = JSON.parse(property.thumbnails);
        } catch (e) {
          property.thumbnails = [];
        }
      }
      
      // Fix address if it was serialized as a string
      if (typeof property.address === 'string') {
        try {
          property.address = JSON.parse(property.address);
        } catch (e) {
          property.address = {
            street: '',
            city: '',
            state: 'AZ',
            zip: ''
          };
        }
      }
      
      // Fix listingAgent if it was serialized as a string
      if (typeof property.listingAgent === 'string') {
        try {
          property.listingAgent = JSON.parse(property.listingAgent);
        } catch (e) {
          property.listingAgent = undefined;
        }
      }
      
      setHoveredProperty(property);
      if (mapRef.current) {
        mapRef.current.getCanvas().style.cursor = 'pointer';
      }
    } else {
      setHoveredProperty(null);
      if (mapRef.current) {
        mapRef.current.getCanvas().style.cursor = '';
      }
    }
  }, []);

  // Toggle layer visibility
  const toggleLayer = useCallback((layerId: string) => {
    setLayers(prev => ({
      ...prev,
      [layerId]: !prev[layerId as keyof typeof prev]
    }));
  }, []);

  // Handle map load
  const handleMapLoad = useCallback(() => {
    console.log('✅ Map loaded successfully!');
    setMapLoaded(true); // Mark map as loaded
    
    // Create a custom house marker
    const map = mapRef.current?.getMap();
    if (map) {
      // Create house icon as canvas
      const size = 40;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      
      if (ctx) {
        // Draw white circle background
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2 - 2, 0, Math.PI * 2);
        ctx.fill();
        
        // Draw house shape
        ctx.fillStyle = '#10b981';
        ctx.strokeStyle = '#10b981';
        ctx.lineWidth = 2;
        
        // House body
        const houseSize = size * 0.6;
        const x = (size - houseSize) / 2;
        const y = size / 2;
        
        // Draw roof (triangle)
        ctx.beginPath();
        ctx.moveTo(size / 2, y - houseSize * 0.4);
        ctx.lineTo(x, y);
        ctx.lineTo(x + houseSize, y);
        ctx.closePath();
        ctx.fill();
        
        // Draw house body (rectangle)
        ctx.fillRect(x + houseSize * 0.1, y, houseSize * 0.8, houseSize * 0.5);
        
        // Draw door
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x + houseSize * 0.4, y + houseSize * 0.2, houseSize * 0.2, houseSize * 0.3);
      }
      
      // Convert canvas to ImageData for Mapbox
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        map.addImage('house-marker', imageData, { pixelRatio: 2 });
      }
    }
    
    // Update visible properties on initial load
    setTimeout(updateVisibleProperties, 100);
  }, [updateVisibleProperties]);
  
  // Update visible properties when properties change
  useEffect(() => {
    if (mapRef.current) {
      updateVisibleProperties();
    }
  }, [properties, updateVisibleProperties]);

  return (
    <div className={`map-view-fullscreen ${showSidebar ? 'with-sidebar' : ''}`}>
      {/* View Mode Toggle */}
      <div className="view-mode-toggle">
        <ButtonGroup>
          <Button
            variant={viewMode === 'map' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => {
              setViewMode('map');
              setShowSidebar(currentZoom > 14);
              setShowPropertyCarousel(true);
            }}
          >
            <FontAwesomeIcon icon={faMap} className="me-1" />
            Map Focus
          </Button>
          <Button
            variant={viewMode === 'browse' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => {
              setViewMode('browse');
              setShowSidebar(true);
              setShowPropertyCarousel(false);
            }}
          >
            <FontAwesomeIcon icon={faList} className="me-1" />
            Browse
          </Button>
          <Button
            variant={viewMode === 'split' ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => {
              setViewMode('split');
              setShowSidebar(true);
              setShowPropertyCarousel(true);
            }}
          >
            <FontAwesomeIcon icon={faTh} className="me-1" />
            Split View
          </Button>
        </ButtonGroup>
        
        {/* Tour Button */}
        {tourPropertyIds.size > 0 && (
          <Button
            variant="success"
            size="sm"
            className="ms-2"
            onClick={() => setShowTourSchedulingModal(true)}
          >
            <FontAwesomeIcon icon={faCalendarCheck} className="me-2" />
            Schedule Tour ({tourPropertyIds.size})
          </Button>
        )}
      </div>

      
      {/* Full Screen Map */}
      <Map
        ref={mapRef}
        {...viewState}
        onMove={evt => {
          setViewState(evt.viewState);
          setPreviousZoom(currentZoom);
          setCurrentZoom(evt.viewState.zoom);
        }}
        onMoveEnd={() => {
          // Only update visible properties after movement stops
          updateVisibleProperties();
        }}
        onMouseMove={handleMouseMove}
        onClick={handleMapClick}
        onLoad={handleMapLoad}
        mapStyle={getMapStyleUrl()}
        mapboxAccessToken={MAPBOX_TOKEN}
        interactiveLayerIds={['clusters', 'unclustered-point', 'seller-property-bg', 'seller-property-icon']}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
        maxTileCacheSize={50}
        trackResize={false}
      >
        {/* Navigation Controls */}
        <NavigationControl position="top-right" />
        <GeolocateControl position="top-right" />
        
        {/* Property Data Source */}
        <Source
          id="properties"
          type="geojson"
          data={propertyGeoJSON}
          cluster={true}
          clusterMaxZoom={14}
          clusterRadius={50}
        >
          <Layer {...clusterLayer} />
          <Layer {...clusterCountLayer} />
          <Layer {...unclusteredPointLayer} />
        </Source>

        {/* Highlighted Property Marker */}
        {highlightedProperty && (
          <Source
            id="highlighted-property"
            type="geojson"
            data={{
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [highlightedProperty.coordinates?.longitude || 0, highlightedProperty.coordinates?.latitude || 0]
              },
              properties: {}
            }}
          >
            {/* Pulsing outer ring */}
            <Layer
              id="highlighted-property-pulse"
              type="circle"
              paint={{
                'circle-radius': 45,
                'circle-color': '#10b981',
                'circle-opacity': 0.2,
                'circle-pitch-alignment': 'map'
              }}
            />
            {/* Background circle for the house icon */}
            <Layer
              id="highlighted-property-bg"
              type="circle"
              paint={{
                'circle-radius': 35,
                'circle-color': '#10b981', // Green background
                'circle-stroke-width': 4,
                'circle-stroke-color': '#ffffff',
                'circle-opacity': 1,
                'circle-pitch-alignment': 'map'
              }}
            />
            {/* House icon using Font Awesome */}
            <Layer
              id="highlighted-property-icon"
              type="symbol"
              layout={{
                'icon-image': 'house-marker',
                'icon-size': 1.5,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-anchor': 'center'
              }}
            />
          </Source>
        )}


        {/* Seller Property Overlay */}
        {showSellerOverlay && sellerProperty && sellerProperty.valuation && (
          <Source
            id="seller-property"
            type="geojson"
            data={{
              type: 'Feature',
              geometry: {
                type: 'Point',
                coordinates: [
                  sellerProperty.valuation.longitude || -112.0740, 
                  sellerProperty.valuation.latitude || 33.4484
                ]
              },
              properties: {}
            }}
          >
            {/* Pulsing outer ring for seller property */}
            <Layer
              id="seller-property-pulse"
              type="circle"
              paint={{
                'circle-radius': 60,
                'circle-color': '#8b5cf6', // Purple for seller
                'circle-opacity': 0.3,
                'circle-pitch-alignment': 'map'
              }}
            />
            {/* Background circle for seller property */}
            <Layer
              id="seller-property-bg"
              type="circle"
              paint={{
                'circle-radius': 45,
                'circle-color': '#8b5cf6', // Purple background
                'circle-stroke-width': 4,
                'circle-stroke-color': '#ffffff',
                'circle-opacity': 1,
                'circle-pitch-alignment': 'map'
              }}
            />
            {/* House icon for seller property */}
            <Layer
              id="seller-property-icon"
              type="symbol"
              layout={{
                'icon-image': 'house-marker',
                'icon-size': 2,
                'icon-allow-overlap': true,
                'icon-ignore-placement': true,
                'icon-anchor': 'center'
              }}
            />
            {/* Price label for seller property */}
            <Layer
              id="seller-property-label"
              type="symbol"
              layout={{
                'text-field': `$${(sellerProperty.valuation.estimated_value / 1000).toFixed(0)}k`,
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-size': 16,
                'text-offset': [0, 2.5],
                'text-anchor': 'top'
              }}
              paint={{
                'text-color': '#8b5cf6',
                'text-halo-color': '#ffffff',
                'text-halo-width': 2
              }}
            />
          </Source>
        )}

        {/* Selected Property Popup - Removed in favor of modal */}
      </Map>

      {/* Hover Card */}
      {hoveredProperty && (
        <PropertyHoverCard
          property={hoveredProperty}
          x={cursorPosition.x}
          y={cursorPosition.y}
          thetaInsight={hoveredProperty.mlsNumber ? thetaInsights?.[hoveredProperty.mlsNumber] : undefined}
        />
      )}

      {/* Voice Control */}
      <VoiceControl onCommand={handleVoiceCommand} />

      {/* Map Controls Container */}
      <div className="map-controls-container">
        <MapStyleControls
          mapStyle={mapStyle}
          onStyleChange={handleStyleChange}
        />
        <LayerControls
          layers={layers}
          onToggleLayer={toggleLayer}
        />
      </div>


      {/* Agent K - AI Real Estate Assistant */}
      <AgentK
        validProperties={validProperties}
        externalCriteria={searchFilters}
        onSearchCriteria={(criteria) => {
          console.log('🔍 AI Search criteria:', criteria);
          
          // Apply filters to the map
          setSearchFilters(criteria);
          
          // Priority: Check for zip code first, then city
          if (criteria.zipCode || criteria.zip) {
            const zipCode = criteria.zipCode || criteria.zip;
            const zipCoords: { [key: string]: { lat: number, lng: number } } = {
              // Phoenix area zip codes
              '85001': { lat: 33.4484, lng: -112.0740 },
              '85003': { lat: 33.4507, lng: -112.0775 },
              '85004': { lat: 33.4510, lng: -112.0712 },
              '85006': { lat: 33.4684, lng: -112.0475 },
              '85007': { lat: 33.4369, lng: -112.0874 },
              '85008': { lat: 33.4653, lng: -112.0230 },
              '85009': { lat: 33.4511, lng: -112.1380 },
              '85013': { lat: 33.5090, lng: -112.0844 },
              '85014': { lat: 33.5095, lng: -112.0475 },
              '85015': { lat: 33.5074, lng: -112.1022 },
              '85016': { lat: 33.5312, lng: -112.0327 },
              '85018': { lat: 33.5090, lng: -111.9785 },
              '85020': { lat: 33.5641, lng: -112.0475 },
              '85021': { lat: 33.5641, lng: -112.0844 },
              '85022': { lat: 33.6055, lng: -112.0475 },
              '85032': { lat: 33.6256, lng: -112.0060 },
              // Scottsdale
              '85250': { lat: 33.4890, lng: -111.9208 },
              '85251': { lat: 33.4942, lng: -111.9261 },
              '85254': { lat: 33.6214, lng: -111.9261 },
              '85255': { lat: 33.7382, lng: -111.8577 },
              '85257': { lat: 33.4680, lng: -111.9261 },
              '85258': { lat: 33.5095, lng: -111.8902 },
              '85259': { lat: 33.7100, lng: -111.8653 },
              '85260': { lat: 33.6334, lng: -111.9252 },
              // Mesa
              '85201': { lat: 33.4222, lng: -111.8226 },
              '85202': { lat: 33.4019, lng: -111.8726 },
              '85203': { lat: 33.4152, lng: -111.7890 },
              '85204': { lat: 33.3996, lng: -111.7666 },
              '85205': { lat: 33.4148, lng: -111.7019 },
              '85206': { lat: 33.3901, lng: -111.6828 },
              '85207': { lat: 33.4477, lng: -111.6447 },
              '85208': { lat: 33.3978, lng: -111.6433 },
              '85209': { lat: 33.4513, lng: -111.6829 },
              '85210': { lat: 33.3826, lng: -111.8419 },
              '85212': { lat: 33.3516, lng: -111.5499 },
              '85213': { lat: 33.4335, lng: -111.7594 },
              '85215': { lat: 33.4300, lng: -111.5600 },
              // Chandler
              '85224': { lat: 33.3356, lng: -111.9537 },
              '85225': { lat: 33.2803, lng: -111.8947 },
              '85226': { lat: 33.2930, lng: -111.8411 },
              '85248': { lat: 33.2467, lng: -111.7890 },
              '85249': { lat: 33.2544, lng: -111.7342 },
              '85286': { lat: 33.2628, lng: -111.9400 },
              // Gilbert
              '85233': { lat: 33.3642, lng: -111.7340 },
              '85234': { lat: 33.3528, lng: -111.7890 },
              '85295': { lat: 33.3832, lng: -111.6993 },
              '85296': { lat: 33.3100, lng: -111.7600 },
              '85297': { lat: 33.2767, lng: -111.6829 },
              '85298': { lat: 33.2300, lng: -111.6400 },
              // Tempe
              '85281': { lat: 33.4255, lng: -111.9400 },
              '85282': { lat: 33.4045, lng: -111.9269 },
              '85283': { lat: 33.4353, lng: -111.9089 },
              '85284': { lat: 33.3442, lng: -111.9008 }
            };
            
            if (zipCoords[zipCode]) {
              console.log('📍 Flying to ZIP code:', zipCode);
              mapRef.current?.flyTo({
                center: [zipCoords[zipCode].lng, zipCoords[zipCode].lat],
                zoom: 14, // Closer zoom for zip code
                duration: 2000
              });
              return; // Exit early since we found a zip code
            }
          }
          
          // If no zip code, check for city
          if (criteria.city) {
            const locationCoords: { [key: string]: { lat: number, lng: number } } = {
              'Phoenix': { lat: 33.4484, lng: -112.0740 },
              'Scottsdale': { lat: 33.4942, lng: -111.9261 },
              'Tempe': { lat: 33.4255, lng: -111.9400 },
              'Mesa': { lat: 33.4152, lng: -111.8315 },
              'Chandler': { lat: 33.3062, lng: -111.8413 },
              'Gilbert': { lat: 33.3528, lng: -111.7890 },
              'Glendale': { lat: 33.5387, lng: -112.1859 },
              'Peoria': { lat: 33.5806, lng: -112.2374 },
              'Surprise': { lat: 33.6292, lng: -112.3679 },
              'Avondale': { lat: 33.4356, lng: -112.3496 },
              'Queen Creek': { lat: 33.2487, lng: -111.6342 },
              'Buckeye': { lat: 33.3703, lng: -112.5838 },
              'Goodyear': { lat: 33.4353, lng: -112.3580 },
              'Paradise Valley': { lat: 33.5312, lng: -111.9426 },
              'Cave Creek': { lat: 33.8333, lng: -111.9508 },
              'Fountain Hills': { lat: 33.6117, lng: -111.7173 }
            };
            
            if (locationCoords[criteria.city]) {
              mapRef.current?.flyTo({
                center: [locationCoords[criteria.city].lng, locationCoords[criteria.city].lat],
                zoom: 12,
                duration: 2000
              });
            }
          }
        }}
        agentPhoto={agentPhoto}
        agentName="Agent K"
      />

      {/* Property Carousel - Bottom */}
      {showPropertyCarousel && visibleProperties.length > 0 && viewMode !== 'browse' && currentZoom <= 14 && (
        <PropertyCarousel
          properties={visibleProperties}
          selectedProperty={selectedProperty}
          onPropertySelect={(property) => {
            setSelectedProperty(property);
            setShowDetailsModal(true); // Show modal instead of panel
            onPropertySelect(property);
            // Fly to selected property if coordinates are valid
            if (property.coordinates && 
                !isNaN(property.coordinates.longitude) && 
                !isNaN(property.coordinates.latitude) &&
                property.coordinates.longitude !== 0 &&
                property.coordinates.latitude !== 0) {
              mapRef.current?.flyTo({
                center: [property.coordinates.longitude, property.coordinates.latitude],
                zoom: 15,
                duration: 1000
              });
            } else {
              console.warn('Invalid coordinates for property:', property.mlsNumber, property.coordinates);
            }
          }}
          onPropertyHover={(property) => {
            setHighlightedProperty(property);
          }}
          onClose={() => setShowPropertyCarousel(false)}
          title={`${visibleProperties.length} Properties in View`}
        />
      )}

      {/* Property List Sidebar */}
      {showSidebar && (
        <div className="property-sidebar">
          <div className="sidebar-header">
            <h5>{visibleProperties.length} Properties</h5>
            {currentZoom > 14 && viewMode === 'map' && (
              <small className="text-muted">Auto-showing in neighborhood view</small>
            )}
          </div>
          <PropertyList
            properties={visibleProperties}
            selectedProperty={selectedProperty}
            onPropertySelect={(property) => {
              setSelectedProperty(property);
              setShowDetailsModal(true); // Show modal instead of panel
              onPropertySelect(property);
              // Fly to selected property if coordinates are valid
              if (property.coordinates && 
                  !isNaN(property.coordinates.longitude) && 
                  !isNaN(property.coordinates.latitude) &&
                  property.coordinates.longitude !== 0 &&
                  property.coordinates.latitude !== 0) {
                mapRef.current?.flyTo({
                  center: [property.coordinates.longitude, property.coordinates.latitude],
                  zoom: 16,
                  duration: 1000
                });
              } else {
                console.warn('Invalid coordinates for property:', property.mlsNumber, property.coordinates);
              }
            }}
            onScheduleTour={(property) => {
              console.log('Schedule tour for:', property);
              setSelectedProperty(property);
              setShowTourSchedulingModal(true);
            }}
            onSaveProperty={onToggleSave}
            savedPropertyIds={savedPropertyIds}
          />
        </div>
      )}

      {/* Property Details Panel - Right Sidebar */}
      {selectedProperty && !showSidebar && (
        <PropertyDetailsPanel
          show={showDetailsPanel}
          properties={[selectedProperty]}
          onHide={() => {
            setShowDetailsPanel(false);
            setSelectedProperty(null);
          }}
          onPropertySelect={onPropertySelect}
          onScheduleTour={(property) => {
            console.log('Schedule tour for:', property);
            setSelectedProperty(property);
            setShowTourSchedulingModal(true);
          }}
          onSaveProperty={(property) => {
            console.log('Save property:', property);
            if (onToggleSave) {
              onToggleSave(property);
            }
          }}
        />
      )}

      {/* Property Details Modal */}
      <PropertyDetailsModal
        show={showDetailsModal}
        property={selectedProperty}
        onHide={() => {
          setShowDetailsModal(false);
          setSelectedProperty(null);
        }}
        onSave={onToggleSave}
        isSaved={selectedProperty && selectedProperty.mlsNumber ? savedPropertyIds.has(selectedProperty.mlsNumber) : false}
        onScheduleTour={(property) => {
          // Toggle property in tour
          const newTourIds = new Set(tourPropertyIds);
          if (property.mlsNumber && newTourIds.has(property.mlsNumber)) {
            newTourIds.delete(property.mlsNumber);
            toast.success('Removed from tour list');
          } else if (property.mlsNumber) {
            newTourIds.add(property.mlsNumber);
            toast.success('Added to tour list! Pick more homes or view your tour anytime.');
          }
          setTourPropertyIds(newTourIds);
          
          // Store in localStorage for persistence
          localStorage.setItem('tourProperties', JSON.stringify(Array.from(newTourIds)));
          
          // Dispatch custom event for same-window updates
          window.dispatchEvent(new Event('tourUpdated'));
        }}
        isInTour={selectedProperty && selectedProperty.mlsNumber ? tourPropertyIds.has(selectedProperty.mlsNumber) : false}
      />

      {/* Tour Scheduling Modal */}
      <TourSchedulingModal
        show={showTourSchedulingModal}
        onHide={() => setShowTourSchedulingModal(false)}
        tourProperties={
          selectedProperty && showTourSchedulingModal
            ? [selectedProperty, ...properties.filter(p => p.mlsNumber && p.mlsNumber !== selectedProperty.mlsNumber && tourPropertyIds.has(p.mlsNumber))]
            : properties.filter(p => p.mlsNumber && tourPropertyIds.has(p.mlsNumber))
        }
        userInfo={{
          name: localStorage.getItem('userName') || undefined,
          email: localStorage.getItem('userEmail') || undefined,
          phone: localStorage.getItem('userPhone') || undefined
        }}
      />

    </div>
  );
};

export default MapView;